import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { venuesAPI, contactsAPI, notesAPI, activitiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  MapPin,
  Pencil,
  Trash2,
  Building2,
  Truck,
  Zap,
  Wifi,
  Cable,
  Users,
  FileText,
  History,
  ArrowLeft,
  Send,
  AlertTriangle,
  Phone,
  Calendar,
  Package,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import LocationMap from '../components/LocationMap';
import LocationFiles from '../components/LocationFiles';

const VenueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [venue, setVenue] = useState(null);
  const [venueContacts, setVenueContacts] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [venueRes, notesRes, activitiesRes, eventsRes] = await Promise.all([
        venuesAPI.get(id),
        notesAPI.list('venue', id),
        activitiesAPI.list('venue', id),
        venuesAPI.getEvents(id),
      ]);
      setVenue(venueRes.data);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
      setLinkedEvents(eventsRes.data);

      // Load venue contacts if organization exists
      if (venueRes.data.organization_id) {
        const contactsRes = await contactsAPI.list({
          organization_id: venueRes.data.organization_id,
          role_tag: 'venue',
        });
        setVenueContacts(contactsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load venue');
      navigate('/venues');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await notesAPI.create({
        entity_type: 'venue',
        entity_id: id,
        content: newNote,
      });
      setNewNote('');
      loadData();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDelete = async () => {
    try {
      await venuesAPI.delete(id);
      toast.success('Venue deleted');
      navigate('/venues');
    } catch (error) {
      toast.error('Failed to delete venue');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!venue) return null;

  const fullAddress = [venue.address, venue.city, venue.state, venue.zip_code]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-6" data-testid="venue-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/venues">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <MapPin className="h-8 w-8 text-amber-400" />
              {venue.location_name}
            </h1>
            {venue.organization_name && (
              <Link
                to={`/organizations/${venue.organization_id}`}
                className="text-muted-foreground hover:text-primary transition-colors mt-1 inline-flex items-center gap-1"
              >
                <Building2 className="h-4 w-4" />
                {venue.organization_name}
              </Link>
            )}
            {fullAddress && (
              <p className="text-muted-foreground mt-1">{fullAddress}</p>
            )}
            {venue.phone && (
              <a
                href={`tel:${venue.phone}`}
                className="text-muted-foreground hover:text-primary transition-colors mt-1 inline-flex items-center gap-1"
              >
                <Phone className="h-4 w-4" />
                {venue.phone}
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-12 sm:ml-0">
          <Button variant="outline" asChild data-testid="edit-venue-btn">
            <Link to={`/locations/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowDelete(true)}
              data-testid="delete-venue-btn"
            >
              <Trash2 className="h-4 w-4 mr-2 text-destructive" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="logistics" className="w-full">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="logistics">Load-In / Logistics</TabsTrigger>
              <TabsTrigger value="technical">Technical</TabsTrigger>
              <TabsTrigger value="vendors">Vendors</TabsTrigger>
              <TabsTrigger value="safety">Safety & Parking</TabsTrigger>
              <TabsTrigger value="files">Files & Photos</TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            {/* Logistics Tab */}
            <TabsContent value="logistics" className="space-y-6 mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Truck className="h-5 w-5 text-cyan-400" />
                    Load-In / Delivery Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {venue.load_in_type?.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                        Load-In Type
                      </p>
                      <div className="flex gap-2">
                        {venue.load_in_type.map((type) => (
                          <Badge key={type} variant="outline" className="badge-venue">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {venue.load_in_address && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Load-In Address
                      </p>
                      <p className="mono text-sm">{venue.load_in_address}</p>
                    </div>
                  )}

                  {venue.dock_measurements && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Dock Measurements
                      </p>
                      <p className="mono text-sm whitespace-pre-wrap">{venue.dock_measurements}</p>
                    </div>
                  )}

                  {venue.dock_notes && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Dock Notes
                      </p>
                      <p className="whitespace-pre-wrap">{venue.dock_notes}</p>
                    </div>
                  )}

                  {venue.elevator_sizes && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Elevator Sizes
                      </p>
                      <p className="mono text-sm whitespace-pre-wrap">{venue.elevator_sizes}</p>
                    </div>
                  )}

                  {venue.push_notes && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Push Route Notes
                      </p>
                      <p className="whitespace-pre-wrap">{venue.push_notes}</p>
                    </div>
                  )}

                  {!venue.load_in_type?.length && !venue.dock_notes && !venue.elevator_sizes && !venue.push_notes && (
                    <p className="text-muted-foreground text-center py-4">
                      No logistics information recorded
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Technical Tab */}
            <TabsContent value="technical" className="space-y-6 mt-6">
              {/* Rigging */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Cable className="h-5 w-5 text-cyan-400" />
                    Rigging
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.rigging_notes ? (
                    <p className="whitespace-pre-wrap">{venue.rigging_notes}</p>
                  ) : (
                    <p className="text-muted-foreground">No rigging notes recorded</p>
                  )}
                </CardContent>
              </Card>

              {/* Power */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-400" />
                    Power
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.power_notes ? (
                    <p className="whitespace-pre-wrap">{venue.power_notes}</p>
                  ) : (
                    <p className="text-muted-foreground">No power notes recorded</p>
                  )}
                </CardContent>
              </Card>

              {/* Internet */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Wifi className="h-5 w-5 text-cyan-400" />
                    Internet / Streaming
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.internet_notes ? (
                    <p className="whitespace-pre-wrap">{venue.internet_notes}</p>
                  ) : (
                    <p className="text-muted-foreground">No internet notes recorded</p>
                  )}
                </CardContent>
              </Card>

              {/* Built in / Available Equipment */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Package className="h-5 w-5 text-emerald-400" />
                    Built in / Available Equipment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.available_equipment ? (
                    <p className="whitespace-pre-wrap">{venue.available_equipment}</p>
                  ) : (
                    <p className="text-muted-foreground">No equipment information recorded</p>
                  )}
                </CardContent>
              </Card>

              {/* Union Requirements */}
              {venue.union_requirements && (
                <Card className="border-border/50 bg-card/50 border-yellow-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2 text-yellow-400">
                      <AlertTriangle className="h-5 w-5" />
                      Union / Labor Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{venue.union_requirements}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Vendors Tab */}
            <TabsContent value="vendors" className="space-y-6 mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">House Vendors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-background/50">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        AV Vendor
                      </p>
                      <p className="font-medium">{venue.av_vendor || 'Not specified'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-background/50">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Rigging Vendor
                      </p>
                      <p className="font-medium">{venue.rigging_vendor || 'Not specified'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-background/50">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Power Vendor
                      </p>
                      <p className="font-medium">{venue.power_vendor || 'Not specified'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-background/50">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                        Internet Vendor
                      </p>
                      <p className="font-medium">{venue.internet_vendor || 'Not specified'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Venue Contacts */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-cyan-400" />
                    Venue Contacts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venueContacts.length > 0 ? (
                    <div className="space-y-3">
                      {venueContacts.map((contact) => (
                        <Link
                          key={contact.id}
                          to={`/contacts/${contact.id}`}
                          className="block p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                        >
                          <p className="font-medium">{contact.contact_name}</p>
                          {contact.title && (
                            <p className="text-sm text-muted-foreground">{contact.title}</p>
                          )}
                          {contact.email && (
                            <p className="text-sm text-muted-foreground">{contact.email}</p>
                          )}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      No venue contacts found
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Safety & Parking Tab */}
            <TabsContent value="safety" className="space-y-6 mt-6">
              {/* Parking Information */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Truck className="h-5 w-5 text-amber-400" />
                    Parking Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.parking_options ? (
                    <div className="space-y-4">
                      <p className="whitespace-pre-wrap">{venue.parking_options}</p>
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-400 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Vehicles are parked at owners risk. Parking is not reimbursed unless noted.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      No parking information recorded
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Emergency Room Information */}
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-400" />
                    Nearest Emergency Room
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.nearest_er_name ? (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                          Hospital / ER
                        </p>
                        <p className="font-medium text-lg">{venue.nearest_er_name}</p>
                      </div>
                      {venue.nearest_er_address && (
                        <div>
                          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                            Address
                          </p>
                          <p className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                            {venue.nearest_er_address}
                          </p>
                        </div>
                      )}
                      {venue.nearest_er_phone && (
                        <div>
                          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                            Phone
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <a 
                              href={`tel:${venue.nearest_er_phone}`}
                              className="text-red-400 hover:text-red-300 font-medium"
                            >
                              {venue.nearest_er_phone}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      No emergency room information recorded
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" className="space-y-6 mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-emerald-400" />
                    Linked Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {linkedEvents.length > 0 ? (
                    <Table data-testid="linked-events-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event Name</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Client</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {linkedEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <Link
                                to={`/events/${event.id}`}
                                className="font-medium hover:text-primary transition-colors"
                                data-testid={`event-link-${event.id}`}
                              >
                                {event.event_name}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {event.start_date
                                ? format(new Date(event.start_date), 'MMM d, yyyy')
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  event.status === 'completed'
                                    ? 'badge-partner'
                                    : event.status === 'in_progress'
                                    ? 'badge-vendor'
                                    : event.status === 'confirmed'
                                    ? 'badge-venue'
                                    : 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400'
                                }
                              >
                                {event.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {event.client_name || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-center py-6">
                      No events have been held at this venue
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Files & Photos Tab */}
            <TabsContent value="files" className="space-y-6 mt-6">
              <LocationFiles locationId={id} />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-6 mt-6">
              {venue.other_notes && (
                <Card className="border-border/50 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Other Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{venue.other_notes}</p>
                  </CardContent>
                </Card>
              )}

              {venue.lessons_learned && (
                <Card className="border-border/50 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Lessons Learned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{venue.lessons_learned}</p>
                  </CardContent>
                </Card>
              )}

              {!venue.other_notes && !venue.lessons_learned && (
                <Card className="border-border/50 bg-card/50">
                  <CardContent className="py-8">
                    <p className="text-muted-foreground text-center">
                      No additional notes recorded
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Column - Map, Notes & Activity */}
        <div className="space-y-6">
          {/* Location Map */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-amber-400" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LocationMap
                address={venue.street_address || venue.address}
                city={venue.city}
                state={venue.state}
                zipCode={venue.zip_code}
                locationName={venue.location_name}
              />
            </CardContent>
          </Card>

          {/* Add Note */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Add Note
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Write a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="bg-background/50 min-h-[100px]"
                data-testid="note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="w-full"
                data-testid="add-note-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </CardContent>
          </Card>

          {/* Notes History */}
          {notes.length > 0 && (
            <Card className="border-border/50 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Note History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px] pr-4">
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className="p-3 rounded-lg bg-background/50">
                        <p className="whitespace-pre-wrap text-sm">{note.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {note.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Activity History */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <History className="h-5 w-5 text-cyan-400" />
                History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px] pr-4">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div key={activity.id} className="text-sm">
                        <p>
                          <span className="font-medium">{activity.action}</span>
                          {' - '}
                          {activity.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </p>
                        <Separator className="mt-3" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No history yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Venue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{venue.location_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VenueDetail;
